import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { api } from '../../api/axios'

const announcementAPI = {
  getActive: () => api.get('/announcements/active'),
}

const FALLBACK = [
  {
    id: 'f1',
    text: '🚚 Free shipping on orders over ₱999! Shop now →',
    link: '/products',
    active: true,
    displayOrder: 0,
  },
]

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([])
  const [current, setCurrent] = useState(0)
  const [dismissed, setDismissed] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    announcementAPI.getActive()
      .then(res => {
        const items = (res.data?.data || res.data || []).filter(a => a.active !== false)
        setAnnouncements(items.length === 0 ? FALLBACK : items)
      })
      .catch(() => setAnnouncements(FALLBACK))
  }, [])

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (announcements.length <= 1 || isPaused) return
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % announcements.length)
    }, 4000)
  }, [announcements.length, isPaused])

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  useEffect(() => { startTimer(); return () => stopTimer() }, [startTimer, stopTimer])

  useEffect(() => {
    if (!isPaused) {
      const t = setTimeout(startTimer, 100)
      return () => clearTimeout(t)
    }
  }, [current, isPaused, startTimer])

  const goToPrev = () => { stopTimer(); setCurrent(prev => (prev - 1 + announcements.length) % announcements.length) }
  const goToNext = () => { stopTimer(); setCurrent(prev => (prev + 1) % announcements.length) }

  if (dismissed || announcements.length === 0) return null

  const item = announcements[current]

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            key="tab"
            initial={{ x: -40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -40, opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsOpen(true)}
            aria-label="Open announcements"
            className="fixed left-0 top-1/2 -translate-y-1/2 z-50"
            style={{ writingMode: 'vertical-rl' }}
          >
            <span
              className="flex items-center gap-2 px-3 py-5 text-white text-[12px] font-bold uppercase cursor-pointer select-none"
              style={{
                background: 'linear-gradient(180deg, #ff8c00, #ff6900)',
                borderRadius: '0 10px 10px 0',
                transform: 'rotate(180deg)',
                letterSpacing: '0.1em',
                boxShadow: '3px 0 12px rgba(255,105,0,0.5)',
              }}
            >
              Announcements
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="panel"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed left-0 top-1/2 -translate-y-1/2 z-50 flex"
            onMouseEnter={() => { setIsPaused(true); stopTimer() }}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="relative w-64 rounded-r-2xl shadow-2xl overflow-hidden" style={{ background: '#ff6900' }}>
              <button
                onClick={() => setDismissed(true)}
                className="absolute top-2.5 right-2.5 p-1 text-white/70 hover:text-white transition-colors z-10"
                aria-label="Dismiss"
              >
                <FiX size={14} />
              </button>

              <div className="px-5 pt-5 pb-4 min-h-[100px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.link ? (
                      <a href={item.link} className="block text-sm font-medium text-white leading-relaxed hover:underline underline-offset-2">
                        {item.text}
                      </a>
                    ) : (
                      <p className="text-sm font-medium text-white leading-relaxed">{item.text}</p>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {announcements.length > 1 && (
                <div className="flex items-center justify-between px-4 pb-4">
                  <button onClick={goToPrev} className="p-1 text-white/70 hover:text-white transition-colors" aria-label="Previous">
                    <FiChevronLeft size={14} />
                  </button>
                  <div className="flex gap-1.5">
                    {announcements.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => { stopTimer(); setCurrent(i) }}
                        className="w-1.5 h-1.5 rounded-full transition-all duration-200"
                        style={{ background: '#fff', opacity: i === current ? 1 : 0.35 }}
                        aria-label={`Announcement ${i + 1}`}
                      />
                    ))}
                  </div>
                  <button onClick={goToNext} className="p-1 text-white/70 hover:text-white transition-colors" aria-label="Next">
                    <FiChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>

            <button onClick={() => setIsOpen(false)} aria-label="Close announcements">
              <span
                className="flex items-center justify-center w-5 text-white/80 hover:text-white transition-colors"
                style={{ background: '#e05d00', borderRadius: '0 8px 8px 0', padding: '18px 4px' }}
              >
                <FiChevronLeft size={13} />
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default AnnouncementBanner